/**
 * Adobe Commerce I/O Events Configuration Component
 *
 * <license header>
 */

import AdobeCommerceClient from '../../commerce/adobe-commerce-client';
import CustomLogger from '../../framework/custom-logger';
import type { Provider } from '../../io-events/provider/types';
import type { WorkspaceConfig, CommerceEventConfig } from './types';
import ConfigureProvider from './configure-provider';
import {
  EventService,
  EventSubscriptionService,
  type EventSubscription,
} from '@adobe-commerce/aio-services-kit';

/**
 * OnboardCommerce for Adobe Commerce I/O Events Configuration
 *
 * This class provides functionality to automate the configuration of Adobe Commerce
 * I/O Events via REST endpoint, simplifying the onboarding process for Commerce instances.
 *
 * @example
 * ```typescript
 * const adobeCommerceClient = new AdobeCommerceClient(...);
 * const onboardCommerce = new OnboardCommerce(
 *   adobeCommerceClient,
 *   'merchant-id-123',
 *   'environment-id-456',
 *   logger
 * );
 * ```
 */
class OnboardCommerce {
  /** Adobe Commerce client instance */
  private readonly adobeCommerceClient: AdobeCommerceClient;

  /** Merchant ID for Adobe Commerce */
  private readonly merchantId: string;

  /** Environment ID for Adobe Commerce */
  private readonly environmentId: string;

  /** CustomLogger instance for logging operations */
  private readonly customLogger: CustomLogger;

  /** ConfigureProvider instance for provider configuration */
  private readonly configureProvider: ConfigureProvider;

  /** EventSubscriptionService instance for managing event subscriptions */
  private readonly eventSubscriptionService: EventSubscriptionService;

  /** EventService instance for managing events */
  private readonly eventService: EventService;

  /** Flag to indicate if the Commerce instance is PaaS (defaults to false) */
  private readonly isPaaS: boolean;

  /**
   * Constructor for OnboardCommerce
   *
   * @param adobeCommerceClient - Adobe Commerce client instance
   * @param merchantId - Merchant ID for Adobe Commerce
   * @param environmentId - Environment ID for Adobe Commerce
   * @param logger - Optional logger instance for logging operations
   * @param isPaaS - Flag to indicate if the Commerce instance is PaaS (defaults to false)
   * @throws {Error} When required parameters are missing or invalid
   * @example
   * ```typescript
   * const adobeCommerceClient = new AdobeCommerceClient(...);
   * const onboardCommerce = new OnboardCommerce(
   *   adobeCommerceClient,
   *   'merchant-id-123',
   *   'environment-id-456',
   *   logger
   * );
   * ```
   */
  constructor(
    adobeCommerceClient: AdobeCommerceClient,
    merchantId: string,
    environmentId: string,
    logger: any = null,
    isPaaS: boolean = false
  ) {
    // Validate required parameters
    if (!adobeCommerceClient) {
      throw new Error('Adobe Commerce client is required');
    }
    if (!merchantId || typeof merchantId !== 'string') {
      throw new Error('Valid merchant ID is required');
    }
    if (!environmentId || typeof environmentId !== 'string') {
      throw new Error('Valid environment ID is required');
    }

    this.adobeCommerceClient = adobeCommerceClient;
    this.merchantId = merchantId;
    this.environmentId = environmentId;
    this.isPaaS = isPaaS;
    this.customLogger = new CustomLogger(logger);
    this.configureProvider = new ConfigureProvider(
      adobeCommerceClient,
      merchantId,
      environmentId,
      logger
    );
    this.eventSubscriptionService = new EventSubscriptionService(adobeCommerceClient);
    this.eventService = new EventService(adobeCommerceClient);
  }

  /**
   * Process Adobe Commerce I/O Events Configuration
   *
   * This method automates the configuration of Adobe Commerce I/O Events by setting up
   * the provider, workspace, and commerce-specific event configurations.
   *
   * @param provider - Provider configuration for Adobe I/O Events
   * @param workspaceConfig - Workspace configuration settings
   * @param commerceEventsConfig - Array of commerce event configurations (optional, defaults to empty array)
   * @returns A promise that resolves with the configuration result
   * @throws {Error} If configuration fails
   * @example
   * ```typescript
   * const result = await onboardCommerce.process(
   *   providerConfig,
   *   workspaceConfig,
   *   commerceEventsConfig
   * );
   * ```
   */
  async process(
    provider: Provider,
    workspaceConfig: WorkspaceConfig,
    commerceEventsConfig: CommerceEventConfig[] = []
  ): Promise<any> {
    this.customLogger.info(
      `[START] Configuring Adobe Commerce I/O Events\n` +
        `  Provider: ${provider.label} (${provider.id})\n` +
        `  Workspace: ${workspaceConfig.project.name}\n` +
        `  Events to process: ${commerceEventsConfig.length}`
    );

    try {
      // Configure the provider using the ConfigureProvider component
      const providerResult = await this.configureProvider.execute(provider, workspaceConfig);

      if (!providerResult.success) {
        return providerResult;
      }

      // Process commerce events configuration if provided
      if (commerceEventsConfig && commerceEventsConfig.length > 0) {
        // Get current event subscriptions to check what's already configured
        this.customLogger.debug('[FETCH] Fetching current event subscriptions...');
        const eventSubscriptionsResult = await this.eventSubscriptionService.list();

        let existingSubscriptions: any[] = [];
        if (!eventSubscriptionsResult.success) {
          this.customLogger.error(
            `[ERROR] Failed to fetch event subscriptions: ${eventSubscriptionsResult.error}`
          );
        } else {
          existingSubscriptions = Array.isArray(eventSubscriptionsResult.data)
            ? eventSubscriptionsResult.data
            : [];
          this.customLogger.debug(
            `[FETCH] Retrieved ${existingSubscriptions.length} existing event subscription(s)`
          );
        }

        // Get supported events to validate commerce events configuration
        // Skip this step for PaaS instances as they don't require supported events validation
        let supportedEvents: any[] = [];
        if (!this.isPaaS) {
          this.customLogger.debug('[FETCH] Fetching supported events list...');
          const supportedEventsResult = await this.eventService.supportedList();

          if (!supportedEventsResult.success) {
            this.customLogger.error(
              `[ERROR] Failed to fetch supported events: ${supportedEventsResult.error}`
            );
          } else {
            supportedEvents = Array.isArray(supportedEventsResult.data)
              ? supportedEventsResult.data
              : [];
            this.customLogger.debug(
              `[FETCH] Retrieved ${supportedEvents.length} supported event(s)`
            );
          }
        } else {
          this.customLogger.debug('[SKIP] Skipping supported events validation for PaaS instance');
        }

        // Filter events based on existing subscriptions and supported events
        const { alreadySubscribed, needsSubscription, unsupported } =
          this.filterEventsBySubscriptionStatus(
            commerceEventsConfig,
            existingSubscriptions,
            provider.id,
            supportedEvents
          );

        const result = {
          successfulSubscriptions: [] as string[],
          failedSubscriptions: [] as string[],
          alreadySubscribed: alreadySubscribed.map(event => event.event.name),
          unsupported: unsupported.map(event => event.event?.name || 'Unknown'),
          skipped: alreadySubscribed.length + unsupported.length,
        };

        // Process events that need subscription
        for (const commerceEvent of needsSubscription) {
          try {
            // Prepare the event payload with proper transformations
            const preparedEvent = this.prepareEventPayload(commerceEvent, provider.id);

            this.customLogger.debug(
              `[DEBUG] Subscribing to event with payload:\n${JSON.stringify(preparedEvent.event, null, 2)}`
            );

            // Create the event subscription
            const eventSubscribeResult = await this.eventSubscriptionService.create(
              preparedEvent.event as EventSubscription
            );

            if (!eventSubscribeResult.success) {
              result.failedSubscriptions.push(commerceEvent.event.name);
              this.customLogger.error(
                `[ERROR] Failed to subscribe to event: ${commerceEvent.event.name} - ${eventSubscribeResult.error}`
              );
              continue;
            }

            this.customLogger.info(`[CREATE] Successfully subscribed: ${commerceEvent.event.name}`);
            result.successfulSubscriptions.push(commerceEvent.event.name);
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            result.failedSubscriptions.push(commerceEvent.event?.name || 'Unknown');
            this.customLogger.error(
              `[ERROR] Error processing event subscription for ${commerceEvent.event?.name || 'Unknown'}: ${errorMessage}`
            );
          }
        }

        // Log summary of event subscriptions
        this.logEventSubscriptionSummary(result, provider.label);

        // Log important post-subscription steps for PaaS instances
        if (this.isPaaS) {
          this.customLogger.info('[IMPORTANT] ⚠️  Post-Subscription Steps for PaaS:');
          this.customLogger.info(
            '[IMPORTANT] 1. Run: bin/magento events:generate:module to generate module after successful event subscription'
          );
          this.customLogger.info(
            '[IMPORTANT] 2. Run: bin/magento setup:upgrade && bin/magento setup:di:compile && bin/magento cache:flush to install the generated module'
          );
        }
      }

      return {
        success: true,
        message: 'Configuration completed successfully',
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.customLogger.error(`[ERROR] Configuration failed: ${errorMessage}`);
      throw new Error(`Failed to configure Adobe Commerce I/O Events: ${errorMessage}`);
    }
  }

  /**
   * Filters commerce events configuration based on existing subscriptions and supported events
   *
   * @param commerceEventsConfig - Array of commerce event configurations
   * @param existingSubscriptions - Array of existing event subscriptions
   * @param providerId - Provider ID to match against
   * @param supportedEvents - Array of supported events from Adobe Commerce
   * @returns Object containing alreadySubscribed, needsSubscription, and unsupported arrays
   * @private
   */
  private filterEventsBySubscriptionStatus(
    commerceEventsConfig: CommerceEventConfig[],
    existingSubscriptions: any[],
    providerId: string,
    supportedEvents: any[] = []
  ): {
    alreadySubscribed: CommerceEventConfig[];
    needsSubscription: CommerceEventConfig[];
    unsupported: CommerceEventConfig[];
  } {
    const alreadySubscribed: CommerceEventConfig[] = [];
    const needsSubscription: CommerceEventConfig[] = [];
    const unsupported: CommerceEventConfig[] = [];

    // Create a set of supported event names for faster lookup
    const supportedEventNames = new Set(supportedEvents.map(event => event.name));

    commerceEventsConfig.forEach(commerceEvent => {
      const eventName = commerceEvent.event?.name;

      if (!eventName) {
        this.customLogger.error(
          'Commerce event configuration missing event name, skipping:',
          commerceEvent
        );
        return;
      }

      // First check if the event is supported by Adobe Commerce
      if (supportedEvents.length > 0 && !supportedEventNames.has(eventName)) {
        unsupported.push(commerceEvent);
        return;
      }

      // Check if this event is already subscribed for this provider
      const isAlreadySubscribed = existingSubscriptions.some(
        subscription => subscription.name === eventName && subscription.provider_id === providerId
      );

      if (isAlreadySubscribed) {
        alreadySubscribed.push(commerceEvent);
      } else {
        needsSubscription.push(commerceEvent);
      }
    });

    return {
      alreadySubscribed,
      needsSubscription,
      unsupported,
    };
  }

  /**
   * Prepares event payload by transforming event names and adding provider information
   *
   * For PaaS instances, the parent field is omitted to support native events (observer.*, plugin.*)
   * which would be rejected by Adobe Commerce as invalid aliases if parent is set to the same name.
   * For non-PaaS instances, the parent field is set to the event name as usual.
   *
   * @param eventSpec - Event specification object containing event details
   * @param providerId - Provider ID to assign to the event
   * @returns Modified event specification with updated event properties
   * @private
   */
  private prepareEventPayload(eventSpec: CommerceEventConfig, providerId: string): any {
    // Input validation
    if (!eventSpec || !eventSpec.event) {
      throw new Error('Invalid event specification: event object is required');
    }

    if (!eventSpec.event.name) {
      throw new Error('Invalid event specification: event name is required');
    }

    if (!providerId || typeof providerId !== 'string') {
      throw new Error('Valid provider ID is required');
    }

    // Create a deep copy to avoid modifying the original object
    const modifiedEventSpec = JSON.parse(JSON.stringify(eventSpec));
    const eventName = modifiedEventSpec.event.name;

    // For PaaS instances, don't set parent field to support native events
    // For non-PaaS instances, set parent as usual
    if (!this.isPaaS) {
      modifiedEventSpec.event.parent = eventName;
    }
    // Note: For PaaS instances, we intentionally omit the parent field

    modifiedEventSpec.event.provider_id = providerId;
    modifiedEventSpec.event.destination = 'default';
    modifiedEventSpec.event.provider_id = providerId;
    modifiedEventSpec.event.priority = true;
    modifiedEventSpec.event.hipaa_audit_required = false;

    // Ensure rules array exists (required by Adobe Commerce API)
    if (!modifiedEventSpec.event.rules) {
      modifiedEventSpec.event.rules = [];
    }

    return modifiedEventSpec;
  }

  /**
   * Logs a comprehensive summary of event subscription results
   *
   * @param result - Event subscription processing results
   * @param providerLabel - Provider label for display
   * @private
   */
  private logEventSubscriptionSummary(
    result: {
      successfulSubscriptions: string[];
      failedSubscriptions: string[];
      alreadySubscribed: string[];
      unsupported: string[];
      skipped: number;
    },
    providerLabel: string
  ): void {
    // Log individual events with prefixes during processing
    if (result.alreadySubscribed.length > 0) {
      result.alreadySubscribed.forEach(eventName => {
        this.customLogger.info(`[SKIP] Already subscribed: ${eventName}`);
      });
    }

    if (result.unsupported.length > 0) {
      result.unsupported.forEach(eventName => {
        this.customLogger.error(`[ERROR] Unsupported event: ${eventName}`);
      });
    }

    // Summary section (no prefix)
    this.customLogger.info('');
    this.customLogger.info('='.repeat(60));
    this.customLogger.info(`📊 COMMERCE EVENTS CONFIGURATION SUMMARY - ${providerLabel}`);
    this.customLogger.info('='.repeat(60));
    this.customLogger.info('');

    // Overall summary
    const totalProcessed =
      result.successfulSubscriptions.length +
      result.failedSubscriptions.length +
      result.alreadySubscribed.length +
      result.unsupported.length;
    this.customLogger.info(
      `📈 OVERALL: ${totalProcessed} processed | ${result.successfulSubscriptions.length} created | ${result.alreadySubscribed.length} existing | ${result.unsupported.length} unsupported | ${result.failedSubscriptions.length} failed`
    );
    this.customLogger.info('');

    // Successful subscriptions
    if (result.successfulSubscriptions.length > 0) {
      this.customLogger.info(
        `✅ SUCCESSFUL SUBSCRIPTIONS (${result.successfulSubscriptions.length}):`
      );
      result.successfulSubscriptions.forEach(eventName => {
        this.customLogger.info(`   ✓ ${eventName}`);
      });
      this.customLogger.info('');
    }

    // Already subscribed
    if (result.alreadySubscribed.length > 0) {
      this.customLogger.info(`ℹ️  ALREADY SUBSCRIBED (${result.alreadySubscribed.length}):`);
      result.alreadySubscribed.forEach(eventName => {
        this.customLogger.info(`   → ${eventName}`);
      });
      this.customLogger.info('');
    }

    // Unsupported events
    if (result.unsupported.length > 0) {
      this.customLogger.info(`⚠️  UNSUPPORTED EVENTS (${result.unsupported.length}):`);
      result.unsupported.forEach(eventName => {
        this.customLogger.info(`   ⚠ ${eventName}`);
      });
      this.customLogger.info('');
    }

    // Failed subscriptions
    if (result.failedSubscriptions.length > 0) {
      this.customLogger.info(`❌ FAILED SUBSCRIPTIONS (${result.failedSubscriptions.length}):`);
      result.failedSubscriptions.forEach(eventName => {
        this.customLogger.info(`   ✗ ${eventName}`);
      });
      this.customLogger.info('');
    }

    this.customLogger.info('='.repeat(60));
  }
}

export default OnboardCommerce;
